import React, { Children, cloneElement, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, useMergeRefs } from '@chakra-ui/react';
import PropTypes from 'prop-types';

// 1px tolerance absorbs sub-pixel rounding in scroll/client dimensions.
const isOverflowing = el => (
  el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
);

// The truncating element may be the wrapped element itself (Text/Button with
// noOfLines) or a descendant (e.g. a Text inside a MenuItem).
const hasTruncatedText = el => (
  !!el && [el, ...el.querySelectorAll('*')].some(isOverflowing)
);

const TruncationTooltip = ({
  label,
  children,
  isDisabled = false,
  ...tooltipProps
}) => {
  const elementRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    setIsTruncated(hasTruncatedText(elementRef.current));
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = elementRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, label]);

  const child = Children.only(children);
  const ref = useMergeRefs(child.ref, elementRef);

  return (
    <Tooltip
      hasArrow
      isDisabled={isDisabled || !label || !isTruncated}
      label={label}
      openDelay={300}
      placement="top"
      {...tooltipProps}
    >
      {cloneElement(child, {
        ref,
        // Re-measure on hover so late reflows (e.g. font loading) are caught.
        onMouseEnter: e => {
          measure();
          child.props.onMouseEnter?.(e);
        }
      })}
    </Tooltip>
  );
};

/** Tooltip that only activates when the wrapped element's text is actually
 *  truncated (ellipsis/line-clamp overflow). The single child must accept a
 *  ref that resolves to its DOM element. */
TruncationTooltip.propTypes = {
  /** Full text to show in the tooltip (usually the untruncated label) */
  label: PropTypes.node,
  /** Single element whose rendered text may truncate */
  children: PropTypes.element.isRequired,
  /** Force-disable the tooltip regardless of truncation */
  isDisabled: PropTypes.bool
};

export default TruncationTooltip;
